import { Search } from '@mui/icons-material'
import { ButtonBase, Checkbox } from '@mui/material'
import { FormEvent, MouseEvent, useState } from 'react'
import { BsTrash } from 'react-icons/bs'
import { IoChevronBack, IoChevronForward } from "react-icons/io5"
import Select from './Select'

function objectValues<T extends {}>(obj:T){
    return Object.keys(obj).map((objKey)=>obj[objKey as keyof T])
}


interface MinTableItem{}

type TableHeaders<T extends MinTableItem> = Record<keyof T, string>

interface TableProps<T extends MinTableItem>{
  tableHeaders: TableHeaders<T>
  tableItems: T[]
  title: string
  itemsPerPage: number
  itemsUniqueKey: string
  onItemClick: (item:T)=>void
  onItemsDelete: (selectedItems: T[])=>void
  onItemsPerPageChange: ()=>void
  onNextPage: ()=>void
  onPrevPage: ()=>void
}


export default function DashboardTable<T extends MinTableItem>(
    {
        tableHeaders, 
        tableItems, 
        title,
        itemsPerPage=5, 
        itemsUniqueKey, 
        onItemClick=()=>{}, 
        onItemsDelete=()=>{},
        onItemsPerPageChange=()=>{},
        onNextPage=()=>{},
        onPrevPage=()=>{}
    }:TableProps<T>
) {

    const [selectedItems, setSelectedItems] = useState<T[]>([])


    const handleOnHeaderBoxCheck = (e: MouseEvent<HTMLButtonElement>)=>{
        e.stopPropagation()

        if((selectedItems.length < tableItems.length && selectedItems.length > 0) || selectedItems.length === 0){

            const _selectedItems:T[] = []
            tableItems.forEach((data:any)=>{
                _selectedItems.push(data[itemsUniqueKey])
            })
            setSelectedItems(_selectedItems)        

        }else if(selectedItems.length === tableItems.length){
            setSelectedItems([])
        }
    }

    const handleOnItemsCheck = (e:FormEvent<HTMLElement>, item:T)=>{
        e.stopPropagation()

        const _selectedItems = [...selectedItems]
        
        if(_selectedItems.includes(item)){
            setSelectedItems(_selectedItems.filter((selected)=>selected !== item))
        }else{
            _selectedItems.push(item)
            setSelectedItems(_selectedItems)
        }
        
    }

    
    return (
      <div className="w-75vw">

        <div className="flex w-full justify-between items-center mb-8 text-white">
            <label className="bg-blue-500/20 py-2 px-4 rounded border border-blue-500/60">
                {title}
            </label>

            <div className='flex justify-center items-center py-1 border-b border-b-gray-600 text-white'>
                <div>
                    <Search fontSize="small" className='text-gray-600' />
                    <input 
                        placeholder='...search' 
                        className='bg-white/0 focus:outline-none'
                    />
                </div>
                {
                    selectedItems.length > 0 && (
                        <div className="ml-4 w-9 h-9 flex justify-center items-center border border-red-600/50 rounded bg-red-600">
                            <ButtonBase 
                                title='Delete' sx={{width: "100%", height: "100%"}}
                                onClick={()=>onItemsDelete(selectedItems)}
                            >
                                <BsTrash className='text-white' />
                            </ButtonBase>
                        </div>
                    )
                }
            </div>
        </div>
          
        <div className="overflow-x-auto w-full">

          <table className="text-left w-fit">
              <thead className="border-b border-b-white/30 flex items-center pb-3 text-white w-full">
                    <th>
                        <Check 
                            checked={(selectedItems.length === tableItems.length) && tableItems.length > 0}
                            indeterminate={selectedItems.length < tableItems.length && selectedItems.length > 0}
                            onClick={handleOnHeaderBoxCheck}
                        />
                    </th>
                  {
                    objectValues(tableHeaders).map((headerValue, idx)=>(
                        <th 
                          className="block capitalize overflow-hidden text-ellipsis w-15vw mr-2"
                          key={`tbl_thd_th_${idx}`}
                        >
                            {headerValue.replaceAll("_"," ")}
                        </th>
                    ))
                  }
              </thead>
              <tbody className="flex flex-col mt-4 text-gray-400 w-full">

                    {
                      tableItems.map((data:any, idx)=>(
                        <tr className="border-b border-b-white/20 cursor-pointer duration-150 ease-in-out flex hover:bg-black/10 items-center py-3 transition  w-fit"
                            key={`tbl_tbdy_tr_${idx}`}
                            onClick={()=>onItemClick(data)}
                        >
                            <td>
                                <Check 
                                    checked={selectedItems.includes(data[itemsUniqueKey])}
                                    onClick={(e:MouseEvent<HTMLButtonElement>)=>handleOnItemsCheck(e, data[itemsUniqueKey])}
                                    indeterminate={false}
                                />
                            </td>
                            {
                              objectValues(data).map((entry, idx)=>(
                                <td 
                                  className="block overflow-hidden text-ellipsis w-15vw mr-2"
                                  key={`tbl_tbdy_td_${idx}`}
                                >
                                  {entry} 
                                </td>
                              ))
                            }
                        </tr>
                      ))
                    }
                  
              </tbody>
          </table>
        
        </div>
        <div className="flex justify-end mt-8 w-full">
            
            <Paginator 
                itemsPerPage={itemsPerPage}
                onNextPage={onNextPage}
                onPrevPage={onPrevPage}
                onItemsPerPageChange={onItemsPerPageChange}
            />
        </div>
      </div>
    )
}


const Check = ({checked, indeterminate, onClick}:{checked:boolean, indeterminate:boolean, onClick: (e:MouseEvent<HTMLButtonElement>)=>void})=>{
    return (
        <Checkbox 
            sx={{
                color: 'primary.light',
                '&.Mui-checked':{
                    color: 'secondary.main'
                },
                '&.MuiCheckbox-indeterminate':{
                    color: 'secondary.main'
                },
            }}
            checked={checked}
            indeterminate={indeterminate}
            onClick={onClick}
        />
    )
}

const Paginator = (
    {itemsPerPage, onItemsPerPageChange, onNextPage, onPrevPage}:
    {itemsPerPage:number, onItemsPerPageChange: ()=>void, onNextPage:()=>void, onPrevPage: ()=>void}
)=>{
    return(
        <>
            <div className="flex justify-center items-center mr-2 text-white">
                Items per page: &nbsp;
                <Select
                    data={[
                        {value:5, label: "Five"},
                        {value:10, label: "Ten"},
                        {value:15, label: "Fifteen"},
                    ]}
                    value={itemsPerPage}
                    onChange={onItemsPerPageChange}
                />
            </div>
            <div>
                <div className="bg-secondary/20 border border-secondary/50 flex items-center justify-center rounded text-secondary text-sm">
                    <button 
                        className="active:bg-secondary/50 p-2"
                        onClick={onPrevPage}
                    >
                        <IoChevronBack />
                    </button> 
                    <div className="h-full border-r border-r-secondary/20"/>
                    <button 
                        className="active:bg-secondary/50 p-2"
                        onClick={onNextPage}
                    >
                        <IoChevronForward />
                    </button> 
                </div>
            </div>
        </>
    )
}